import type { DungeonEntity } from '../entities/DungeonEntity';
import { dungeonService } from './DungeonService';
import { msToTicks } from '../utils/timing.utils';
import type { Element } from '../types/enums';

interface ActiveZone {
  cells: { x: number; y: number }[];
  remainingTicks: number;
  intervalTicks: number;
  elapsedSinceTick: number;
  caster: DungeonEntity;
  amount: number;
  type: 'physical' | 'magic';
  element?: Element;
  sourceSpellId?: string;
}

/**
 * Persistent effect zones. Spawned by `spawnPersistentDamageZone`
 * (Onde-Persistante modifier). Ticked in the main loop (Dungeon.tsx).
 */
class ZoneEffectServiceImpl {
  private zones: ActiveZone[] = [];

  spawnZone(opts: {
    cells: { x: number; y: number }[];
    durationMs: number;
    intervalMs: number;
    amount: number;
    type: 'physical' | 'magic';
    element?: Element;
    caster: DungeonEntity;
    sourceSpellId?: string;
  }): void {
    if (opts.cells.length === 0) {
      return;
    }
    this.zones.push({
      cells: opts.cells.map((c) => ({ x: c.x, y: c.y })),
      remainingTicks: msToTicks(opts.durationMs),
      intervalTicks: msToTicks(opts.intervalMs),
      elapsedSinceTick: 0,
      caster: opts.caster,
      amount: opts.amount,
      type: opts.type,
      element: opts.element,
      sourceSpellId: opts.sourceSpellId,
    });
  }

  tick(deltaTime: number): void {
    if (this.zones.length === 0) {
      return;
    }
    for (let i = this.zones.length - 1; i >= 0; i--) {
      const zone = this.zones[i];
      zone.elapsedSinceTick += deltaTime;
      while (zone.elapsedSinceTick >= zone.intervalTicks) {
        zone.elapsedSinceTick -= zone.intervalTicks;
        const caster = zone.caster;
        for (const cell of zone.cells) {
          const entity = dungeonService.getEntityAt(cell.x, cell.y, caster.id);
          if (!entity) {
            continue;
          }
          if (entity.type === caster.type) {
            continue;
          }
          entity.lastDamageSourceSpellId = zone.sourceSpellId;
          entity.lastDamageSourceCasterId = caster.id;
          if (zone.type === 'physical') {
            caster.dealPhysicalDamage(entity, zone.amount, zone.element);
          } else {
            caster.dealMagicDamage(entity, zone.amount, zone.element);
          }
        }
      }
      zone.remainingTicks -= deltaTime;
      if (zone.remainingTicks <= 0) {
        this.zones.splice(i, 1);
      }
    }
  }

  reset(): void {
    this.zones = [];
  }
}

export const zoneEffectService = new ZoneEffectServiceImpl();
